import { createAsyncThunk } from '@reduxjs/toolkit';
import { getErrorPayload } from '@application/helpers/api-helper';
import familyFestApi from '@infrastructure/family-fest-api';
import { mapFamilyFestWaitlistRow } from './map-family-fest-waitlist-row';
export const fetchFamilyFestWaitlist = createAsyncThunk('familyFestWaitlist/fetch', async ({ eventId, req }) => {
    const response = await familyFestApi.getFamilyFestWaitlist(eventId, req);
    const body = response.data;
    const items = (body.items ?? []).map((row) => mapFamilyFestWaitlistRow(row));
    return { items, totalCount: body.totalCount ?? 0, waitlistedCount: body.waitlistedCount ?? 0 };
});
/** Backend validates capacity/eligibility and is authoritative; its message is surfaced on failure. */
export const sendFamilyFestWaitlistInvitationsAsync = createAsyncThunk('familyFestWaitlist/sendInvitations', async ({ eventId, slots }, { rejectWithValue }) => {
    try {
        const response = await familyFestApi.sendFamilyFestWaitlistInvitations(eventId, slots);
        const body = response.data;
        if (!body.isSuccess) {
            return rejectWithValue({ error: body.message, status: response.status });
        }
        return { invited: body.data ?? 0, message: body.message };
    }
    catch (error) {
        return rejectWithValue(getErrorPayload(error));
    }
});
/** Sends the magic-link email to the selected waiting-list people who have not finished the form. */
export const sendFamilyFestWaitlistLinksAsync = createAsyncThunk('familyFestWaitlist/sendLinks', async ({ eventId, invitationIds }, { rejectWithValue }) => {
    try {
        const response = await familyFestApi.sendFamilyFestWaitlistLinks(eventId, invitationIds);
        const body = response.data;
        if (!body.isSuccess) {
            return rejectWithValue({ error: body.message, status: response.status });
        }
        return { sent: body.data ?? 0, message: body.message };
    }
    catch (error) {
        return rejectWithValue(getErrorPayload(error));
    }
});
/** Free waiting-list slots + expected persons of the invitable waiting list (backend-calculated). */
export const fetchFamilyFestWaitlistInvitePreviewAsync = createAsyncThunk('familyFestWaitlist/invitePreview', async (eventId, { rejectWithValue }) => {
    try {
        const response = await familyFestApi.getFamilyFestWaitlistInvitePreview(eventId);
        return response.data;
    }
    catch (error) {
        return rejectWithValue(getErrorPayload(error));
    }
});
/** Released seats of the event: free (released) and held by open invitations (reserved). */
export const fetchFamilyFestCapacitySlotsAsync = createAsyncThunk('familyFestWaitlist/capacitySlots', async (eventId, { rejectWithValue }) => {
    try {
        const response = await familyFestApi.getFamilyFestCapacitySlots(eventId);
        return response.data;
    }
    catch (error) {
        return rejectWithValue(getErrorPayload(error));
    }
});
/** Admin: move free waiting-list slots to regular capacity; the backend rejects more than are free. */
export const convertFamilyFestWaitlistSlotsAsync = createAsyncThunk('familyFestWaitlist/convertSlots', async ({ eventId, count }, { rejectWithValue }) => {
    try {
        const response = await familyFestApi.convertFamilyFestWaitlistSlots(eventId, count);
        const body = response.data;
        if (!body.isSuccess) {
            return rejectWithValue({ error: body.message, status: response.status });
        }
        return { converted: body.data ?? 0, message: body.message };
    }
    catch (error) {
        return rejectWithValue(getErrorPayload(error));
    }
});
/** Admin: run the auto-invite job now instead of waiting for its schedule. Returns the number invited. */
export const runFamilyFestWaitlistAutoInviteAsync = createAsyncThunk('familyFestWaitlist/runAutoInvite', async (_, { rejectWithValue }) => {
    try {
        const response = await familyFestApi.triggerFamilyFestWaitlistAutoInvite();
        return response.data ?? 0;
    }
    catch (error) {
        return rejectWithValue(getErrorPayload(error));
    }
});
/** Admin: change ExpectedNoOfPersons of a waitlisted invitation; backend rejects any other status. */
export const updateFamilyFestWaitlistPersonsAsync = createAsyncThunk('familyFestWaitlist/updatePersons', async ({ invitationId, numberOfPersons }, { rejectWithValue }) => {
    try {
        const response = await familyFestApi.updateFamilyFestWaitlistPersons(invitationId, numberOfPersons);
        const body = response.data;
        if (!body.isSuccess) {
            return rejectWithValue({ error: body.message, status: response.status });
        }
        return body.message;
    }
    catch (error) {
        return rejectWithValue(getErrorPayload(error));
    }
});
/** Manager: reorder the queue. On 409 (queue changed) the backend's message is surfaced as-is. */
export const moveFamilyFestWaitlistEntryAsync = createAsyncThunk('familyFestWaitlist/move', async ({ eventId, request }, { rejectWithValue }) => {
    try {
        const response = await familyFestApi.moveFamilyFestWaitlistEntry(eventId, request);
        const body = response.data;
        if (!body.isSuccess) {
            return rejectWithValue({ error: body.message, status: response.status });
        }
        return body.message;
    }
    catch (error) {
        return rejectWithValue(getErrorPayload(error));
    }
});
/** Admin: remove a waitlisted entry; backend rejects any other status. */
export const deleteFamilyFestWaitlistEntryAsync = createAsyncThunk('familyFestWaitlist/deleteEntry', async (invitationId, { rejectWithValue }) => {
    try {
        const response = await familyFestApi.deleteFamilyFestWaitlistEntry(invitationId);
        const body = response.data;
        if (!body.isSuccess) {
            return rejectWithValue({ error: body.message, status: response.status });
        }
        return body.message;
    }
    catch (error) {
        return rejectWithValue(getErrorPayload(error));
    }
});
